import { useEffect, useState, useMemo, useCallback } from 'react';
import { Event } from 'nostr-tools';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';

/**
 * Local Feed Hook - Fetch content only from Mavililer community
 * Shows content tagged with #mavililer or #mavililersözlük
 */
import { useGlobalModeration } from './useGlobalModeration';

/**
 * Local Feed Hook - Fetch content only from Mavililer community
 * Shows content tagged with #mavililer or #mavililersözlük
 */
export const useLocalFeed = (limit: number = 20) => {
    const [localEvents, setLocalEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLinking, setIsLinking] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const pool = getNostrPool();
    const { blockedEventIds, blockedPubkeys } = useGlobalModeration(); // Global Block List

    // Mavililer relays
    const localRelaySet = RELAYS;

    const refetch = useCallback(() => {
        setRefreshKey(prev => prev + 1);
    }, []);

    const loadMore = useCallback(async () => {
        if (isLinking || isLoading) return;

        const oldestEvent = localEvents[localEvents.length - 1];
        if (!oldestEvent) return;

        setIsLinking(true);

        const filter = {
            kinds: [1],
            '#t': ['mavililer'],
            until: oldestEvent.created_at - 1,
            limit: limit
        };

        const moreEvents: Event[] = [];
        const sub = pool.subscribeMany(localRelaySet, filter, {
            onevent(event: Event) {
                const hashtags = event.tags
                    .filter(t => t[0] === 't')
                    .map(t => t[1].toLowerCase());

                const hasMavililer = hashtags.includes('mavililer') ||
                    hashtags.includes('mavililersözlük') ||
                    hashtags.includes('mavililersozluk');

                if (hasMavililer) {
                    moreEvents.push(event);
                }
            },
            oneose() {
                if (moreEvents.length > 0) {
                    setLocalEvents(prev => {
                        const newEvents = [...prev, ...moreEvents]
                            .sort((a, b) => b.created_at - a.created_at);
                        return newEvents;
                    });
                }
                setIsLinking(false);
                sub.close();
            }
        });

        setTimeout(() => {
            setIsLinking(false);
            sub.close();
        }, 5000);
    }, [localEvents, isLinking, isLoading, limit, localRelaySet, pool]);

    useEffect(() => {
        setIsLoading(true);
        setLocalEvents([]);

        // Fetch all events with mavililer hashtag
        const filter = {
            kinds: [1], // Only short-form notes
            '#t': ['mavililer'],
            limit: limit,
        };

        console.log('[useLocalFeed] Subscribing with filter:', filter);

        // Safety timeout
        const timeout = setTimeout(() => {
            setIsLoading(false);
        }, 5000);

        let eventCount = 0;

        const sub = pool.subscribeMany(localRelaySet, filter, {
            onevent(event: Event) {
                clearTimeout(timeout);
                // Verify it has mavililer or mavililersözlük hashtag
                const hashtags = event.tags
                    .filter(t => t[0] === 't')
                    .map(t => t[1].toLowerCase());

                const hasMavililer = hashtags.includes('mavililer') ||
                    hashtags.includes('mavililersözlük') ||
                    hashtags.includes('mavililersozluk');

                if (!hasMavililer) return;

                eventCount++;
                setLocalEvents((prev) => {
                    if (prev.some((e) => e.id === event.id)) return prev;
                    return [...prev, event]
                        .sort((a, b) => b.created_at - a.created_at)
                        .slice(0, limit * 2); // Keep a bit more for stable filtering
                });
            },
            oneose() {
                clearTimeout(timeout);
                console.log('[useLocalFeed] EOSE received, total events:', eventCount);
                setIsLoading(false);
            },
        });

        return () => {
            clearTimeout(timeout);
            sub.close();
        };
    }, [limit, localRelaySet, pool, refreshKey]);

    // REACTIVE FILTERING PIPELINE
    const filteredEvents = useMemo(() => {
        return localEvents
            .filter(e => {
                if (blockedEventIds.has(e.id)) return false;
                if (blockedPubkeys.has(e.pubkey)) return false;
                return true;
            })
            .slice(0, limit);
    }, [localEvents, blockedEventIds, blockedPubkeys, limit]);

    return { localEvents: filteredEvents, isLoading, isLinking, loadMore, refetch };
};
